import React, { useEffect, useState, useRef } from 'react';
import { Field, FieldHint } from '../ui';

// Country data organization
interface CountryData {
  code: string;
  name: string;
  flag: string;
}

interface CountrySelectorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  countries: Record<string, CountryData[]>;
  error?: boolean;
  helperText?: string;
  id?: string;
  purpose?: 'source' | 'destination';
  defaultContactCountry?: string;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
  label,
  value,
  onChange,
  countries,
  error = false,
  helperText = '',
  id,
  purpose,
  defaultContactCountry,
}) => {
  const [searchText, setSearchText] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Deduplicated list of all countries
  const allCountries = React.useMemo(() => {
    const uniqueCountries = new Map<string, CountryData>();
    Object.values(countries)
      .flat()
      .forEach((country) => {
        if (!uniqueCountries.has(country.code)) {
          uniqueCountries.set(country.code, country);
        }
      });
    return Array.from(uniqueCountries.values());
  }, [countries]);

  const validCountryCodes = React.useMemo(
    () => new Set(allCountries.map((c) => c.code)),
    [allCountries]
  );

  // Set default contact country for source address
  useEffect(() => {
    if (purpose === 'source' && defaultContactCountry && !value) {
      const countryEntry = allCountries.find((c) => c.name === defaultContactCountry);
      if (countryEntry) onChange(countryEntry.code);
    }
  }, [purpose, defaultContactCountry, value, allCountries, onChange]);

  // Reset invalid values
  useEffect(() => {
    if (value && !validCountryCodes.has(value)) {
      console.warn(`Invalid country code "${value}" detected, resetting`);
      onChange('');
    }
  }, [value, validCountryCodes, onChange]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearchText('');
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Focus search input when opening
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => searchInputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Filtered continents/countries based on search
  const searchLower = searchText.trim().toLowerCase();
  const displayedContinents = Object.keys(countries).filter((continent) => {
    if (!searchLower) return true;
    return countries[continent].some(
      (c) =>
        c.name.toLowerCase().includes(searchLower) ||
        c.code.toLowerCase().includes(searchLower)
    );
  });

  const getFilteredCountriesForContinent = (continent: string): CountryData[] => {
    if (!searchLower) return countries[continent];
    return countries[continent].filter(
      (c) =>
        c.name.toLowerCase().includes(searchLower) ||
        c.code.toLowerCase().includes(searchLower)
    );
  };

  const selectedCountry = allCountries.find((c) => c.code === value);

  const handleSelect = (code: string) => {
    if (validCountryCodes.has(code)) {
      onChange(code);
    }
    setOpen(false);
    setSearchText('');
  };

  return (
    <Field label={label} style={{ position: 'relative' }}>
      {/* Trigger button */}
      <button
        id={id}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: `1px solid ${error ? 'var(--err)' : 'var(--line)'}`,
          borderRadius: 6,
          background: 'var(--bg-elev)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 14,
          color: selectedCountry ? 'var(--ink)' : 'var(--ink-4)',
          textAlign: 'left',
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={{ fontSize: 18 }}>
          {selectedCountry ? selectedCountry.flag : '🌐'}
        </span>
        <span style={{ flex: 1 }}>
          {selectedCountry ? selectedCountry.name : 'Select a country'}
        </span>
        <span style={{ color: 'var(--ink-4)', fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          ref={containerRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            background: 'var(--bg-elev)',
            border: '1px solid var(--line)',
            borderRadius: 8,
            boxShadow: 'var(--shd-2)',
            marginTop: 4,
            maxHeight: 320,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Search input */}
          <div style={{ padding: '8px 8px 4px', borderBottom: '1px solid var(--line)' }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search countries..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid var(--line)',
                borderRadius: 6,
                fontSize: 13,
                background: 'var(--bg-tint)',
                color: 'var(--ink)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Country list */}
          <div style={{ overflowY: 'auto', flex: 1 }} role="listbox">
            {displayedContinents.length === 0 ? (
              <div
                style={{ padding: '12px 16px', fontSize: 13, color: 'var(--ink-4)', textAlign: 'center' }}
              >
                No countries match your search
              </div>
            ) : (
              displayedContinents.map((continent) => {
                const filtered = getFilteredCountriesForContinent(continent);
                if (filtered.length === 0) return null;
                return (
                  <React.Fragment key={continent}>
                    <div
                      style={{
                        padding: '6px 12px 4px',
                        fontSize: 11,
                        fontWeight: 700,
                        color: 'var(--ink-4)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        background: 'var(--bg-tint)',
                        borderBottom: '1px solid var(--line)',
                      }}
                    >
                      {continent}
                    </div>
                    {filtered.map((country) => (
                      <button
                        key={`${continent}-${country.code}`}
                        type="button"
                        role="option"
                        aria-selected={country.code === value}
                        onClick={() => handleSelect(country.code)}
                        style={{
                          width: '100%',
                          padding: '8px 16px',
                          border: 'none',
                          background:
                            country.code === value
                              ? 'rgba(43,85,255,0.08)'
                              : 'transparent',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 13,
                          color: country.code === value ? 'var(--brand-blue)' : 'var(--ink)',
                          fontWeight: country.code === value ? 600 : 400,
                          textAlign: 'left',
                        }}
                        onMouseEnter={(e) => {
                          if (country.code !== value) {
                            (e.currentTarget as HTMLButtonElement).style.background =
                              'var(--bg-tint)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (country.code !== value) {
                            (e.currentTarget as HTMLButtonElement).style.background =
                              'transparent';
                          }
                        }}
                      >
                        <span style={{ fontSize: 16 }}>{country.flag}</span>
                        <span>{country.name}</span>
                      </button>
                    ))}
                  </React.Fragment>
                );
              })
            )}
          </div>
        </div>
      )}

      {helperText && (
        <FieldHint style={{ color: error ? 'var(--err)' : 'var(--ink-4)' }}>{helperText}</FieldHint>
      )}
    </Field>
  );
};

export default CountrySelector;
